/**
 * Campaigns fallback fixtures — derived from canonical Demo Brand canon.
 *
 * Used by /campaigns when the real campaigns table is empty or the
 * caller is anonymous. Numbers come from `lib/fixtures/demo-brand.ts`
 * so /campaigns reconciles with /overview, /channels, /creatives, and
 * the per-campaign performance chart.
 *
 * Real campaign rows from /api/campaigns replace these for live users.
 */

import {
  DEMO_CAMPAIGNS,
  DEMO_CREATIVES,
  formatCompactCents,
  formatCompactNumber,
  formatRoas,
} from '@/lib/fixtures/demo-brand'

export type CampaignChannelKey =
  | 'search'
  | 'social'
  | 'tv'
  | 'programmatic'
  | 'audio'
  | 'retail'
  | 'other'

export interface CampaignRow {
  id?: string
  name: string
  channel: CampaignChannelKey
  status: 'Active' | 'Paused'
  goal: 'Conversions' | 'Traffic' | 'Awareness'
  /** Raw values from /api/campaigns. Present for live rows, absent for fixtures. */
  impressions_total?: number
  spend_cents_total?: number
  cpm_cents?: number | null
  roas_raw?: number | null
  /** Display strings (parsed for sorting). */
  impressions: string
  spend: string
  cpm: string
  roas: string
}

export interface CreativeRow {
  id?: string
  name: string
  channel: CampaignChannelKey
  format: 'Video' | 'Image' | 'Text'
}

/** Format a CPM in cents to "$X.XX" display form. */
function formatCpmDisplay(cpmCents: number): string {
  if (cpmCents <= 0) return '—'
  const dollars = cpmCents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars)
}

export const FALLBACK_CAMPAIGNS: CampaignRow[] = DEMO_CAMPAIGNS.map((c) => {
  const cpmCents = c.impressions > 0 ? Math.round((c.spendCents / c.impressions) * 1000) : 0
  return {
    id: c.id,
    name: c.name,
    channel: c.channel as CampaignChannelKey,
    status: c.status === 'Draft' ? 'Paused' : (c.status as 'Active' | 'Paused'),
    goal: c.goal,
    impressions_total: c.impressions,
    spend_cents_total: c.spendCents,
    cpm_cents: cpmCents,
    roas_raw: c.roas,
    impressions: formatCompactNumber(c.impressions),
    spend: formatCompactCents(c.spendCents),
    cpm: formatCpmDisplay(cpmCents),
    roas: formatRoas(c.roas),
  }
})

export const FALLBACK_CREATIVES: CreativeRow[] = DEMO_CREATIVES.map((c) => ({
  id: c.id,
  name: c.name,
  channel: c.channel as CampaignChannelKey,
  format: (c.format === 'Audio' ? 'Video' : c.format) as 'Video' | 'Image' | 'Text',
}))

/** Visual palette per channel — matches the prototype's CHIP map. */
export interface CampaignChannelPalette {
  label: string
  tint: string
  ink: string
  hex: string
  iconKey: 'search' | 'social' | 'tv' | 'programmatic' | 'audio' | 'retail'
}

export const CAMPAIGN_CHANNEL_PALETTE: Record<CampaignChannelKey, CampaignChannelPalette> = {
  search:       { label: 'Search',       tint: '#FFE8A3', ink: '#7A5C00', hex: '#A8780C', iconKey: 'search' },
  social:       { label: 'Social',       tint: '#A6E1FF', ink: '#0A3A5C', hex: '#2D6CDF', iconKey: 'social' },
  tv:           { label: 'TV / CTV',     tint: '#D4C7FF', ink: '#2D1B5E', hex: '#7A5BD6', iconKey: 'tv' },
  programmatic: { label: 'Programmatic', tint: '#C7F5DD', ink: '#0A3D29', hex: '#0A8A6E', iconKey: 'programmatic' },
  audio:        { label: 'Audio',        tint: '#FFE0B5', ink: '#5C3300', hex: '#E0762D', iconKey: 'audio' },
  retail:       { label: 'Retail',       tint: '#FFD1DC', ink: '#7A1F40', hex: '#C2185B', iconKey: 'retail' },
  other:        { label: 'Other',        tint: '#E8E5DA', ink: '#4A463D', hex: '#7A766A', iconKey: 'programmatic' },
}

/** Parse a display string like "$9,120", "1.2M", "640K", "4.3×" to a sortable number. */
export function parseDisplayNumber(s: string): number {
  if (!s) return 0
  const cleaned = s.replace(/[$,×x]/gi, '').trim()
  if (/M$/i.test(cleaned)) return parseFloat(cleaned) * 1_000_000
  if (/K$/i.test(cleaned)) return parseFloat(cleaned) * 1_000
  return parseFloat(cleaned) || 0
}
